<template>
  <div :class="['sms-section', 'sms-container', { ['empty-sms']: !validSms, 'classic': classicCampaignsEnabled }]">
    <div class="message-input"
         style="border-top-left-radius:0">
      <div
        style="border-top-left-radius:0"
        :class="['sms-input', { rounded: isRounded }]" 
        ref="editor" @drop.prevent="false">
      </div>
      <sms-actions
          :activeMessage="activeMessage"
          :messageModuleName="messageModuleName"
          @insertInTextArea="insertInTextArea"
          @updateCampaign="updateCampaign">
      </sms-actions>
    </div>
    <blacklist-modal
        :module-name="messageModuleName"
        :alertedTerm="alertedTerm"
        :showModal="showBlacklistModal"
        @replaceBlacklistedTerm="replaceBlacklistedTerm"
        @updateIgnoredTerms="updateIgnoredTerms">
    </blacklist-modal>
  </div>
</template>

<script>
  import AxiosMixin from '../../mixins/AxiosMixin'
  import BlacklistModal from './BlacklistModal'
  import { isObject, spliceIntoString } from '../../utilities'
  import {mapGetters, mapState} from 'vuex'
  import Quill from 'quill'
  import Delta from 'quill-delta'
  import SubLabel from './SmsSubstitution'
  import SmsActions from './SmsActions'
  import SmsTextChecksMixin from '../../mixins/SmsTextChecksMixin'

  export default {
    props: {
      activeMessage: Boolean,
      isRounded: Boolean,
      messageModuleName: String,
      smsSend: Boolean,
    },

    name: 'Sms',

    components: {
      BlacklistModal,
      SmsActions,
      Quill,
    },

    mixins: [AxiosMixin, SmsTextChecksMixin],

    data() {
      return {
        alertedTerm: null,
        delayedUpdate: null,
        editor: null,
        ignoredTerms: [],
        isValid: false,
        noUserInteraction: true,
        smsDisplay: '',
        smsText: '',
        specialCharacters: [],
        substitutions: {},
        validSms: true
      }
    },

    methods: {
      afterUpdateSms(data) {
        if (isObject(data)) {
          this.specialCharacters = data.non_gsm_characters
          this.updateMessageState('credits', data.concatenated_parts)
          this.smsText = data.sms_content_raw ? data.sms_content_raw : ''
          this.errorUpdateSms()
          this.substitutions = data.substitutions;
          this.handleSubtitutions();
          if (this.getBlotNode()) {
            this.getBlotNode().setAttribute('data-href', this.substitutions.MMS_LINK.value)
          }
          this.$store.dispatch(`${this.messageModuleName}/UPDATE_SMS_CONTENT`, {
            smsContent: this.smsText,
            characterCount: data.characters
          })
        } else {
          this.errorUpdateSms('data returned is invalid')
        }
      },

      checkForBlacklistedTerms() {
        const foundTerms = this.findBlacklistedTerms({ findBlock: true })
        this.updateMessageState('hasBlacklistedWords', foundTerms.length > 0)
      },

      checkForEmojis(smsContent) {
        const text = smsContent || this.smsText || '',
          containsEmojis = this.textContainsEmojis(text)
        this.hasEmoji = containsEmojis || !!this.specialCharacters?.length
        this.updateMessageState('hasEmoji', this.hasEmoji)
      },

    /**
     * Looks for instances of %{MMS_LINK} in the smsContent string and replaces it with a Quill blot
     * @param {string} smsContent - Text to be used in the campaign
     * @param {boolean} skip - Boolean to short circuit the function if necessary
     *
     * _Notes_
     * - To ensure that only 1 blot is made, we check if a tag of .ql-label exists. If it does, don't make another
     * - this.editor is checked to ensure that quill is initiated prior to inserting the blot
     */

      checkForSubstitutions(smsContent, skip = false){
        if (skip) return false
        const isSubDomNodeNull = this.getBlotNode() === null;
        if (smsContent.indexOf("%{MMS_LINK}") !== -1 && isSubDomNodeNull && this.editor) {
          const stub = " %{MMS_LINK}",
                subIdx = smsContent.indexOf(stub);
          this.editor.deleteText(subIdx, stub.length + 1);
          this.editor.insertEmbed(subIdx, 'subLabel', {id: `MMS_LINK_${this.messageId}`, label: this.mockUrl, href: this.substitutions.MMS_LINK.value})
        } else if (document.getElementsByClassName('ql-label').length > 1) {
          // remove all but first instance of tag
          document.querySelectorAll(`.ql-label[data-id="MMS_LINK_${this.messageId}"]`).forEach((x, i) => {
            if (i > 0) {x.remove()}
          })
        }
      },

      clearModal() {
        this.alertedTerm = null
        this.updateMessageState('showBlacklistModal', false)
      },

      configureInvalidCharacters(invalids) {
        if (invalids) {
          invalids.forEach((c) => !this.invalidCharacters.includes(c) && this.invalidCharacters.push(c) )
          this.$emit('updateInvalidCharacters', this.invalidCharacters)
        }
      },

      configUserInteractions(itemObject) {
        const itemKey = this.dispatchSmsItemKey
        this.$store.dispatch('UPDATE_STATE_OBJECT', { stateKey: 'noUserInteractions', itemKey, itemObject})
      },

      delaySmsFilter() {
        if (this.alertedTerm) {return}
        clearTimeout(this.delayedUpdate)
        this.delayedUpdate = setTimeout(() => {
          this.smsDisplay = this.editor.getText()
          this.filterInvalidCharacters()
        }, 500)
      },

      delayUpdateSms($text, shouldUpdate = false) {
        this.smsText = $text
        //this.checkForBlacklistedTerms()
        clearTimeout(this.delayedUpdate)
        this.delayedUpdate = setTimeout(() => this.updateSms($text, shouldUpdate), 500)
      },

      errorUpdateSms(error) {
        if (this.isCampaign) {
          const message = `issue found updating your sms content. ${error}`,
            itemObject = !!error ? {extraClasses: 'error-alert sms-error', message} : {}
          this.$store.dispatch('UPDATE_STATE_OBJECT', {stateKey: 'alerts', itemKey: 'smsApiError', itemObject})
        }
      },

      filterBlacklistedTerms() {
        const foundTerms = this.findBlacklistedTerms({ useIgnoredList: true })
        foundTerms.sort((a, b) => (a.index > b.index) ? 1 : -1)
        this.alertedTerm = foundTerms.length > 0 ? foundTerms[0] : null
        foundTerms.length > 0 ? this.loadQuill(false) : this.filterInvalidCharacters()
      },

      filterInvalidCharacters() {
        const $text = this.editor.getText()
        this.updateInteraction()
        this.checkForEmojis($text)
        const textToUpdate = this.textFilteredInvalidCharacters($text)
        this.delayUpdateSms(textToUpdate)
      },

    /**
     * Returns the index of the blot when given a domNode
     * @param {Node} blotDomNode - dom node that matches class .ql-label and data-id of message Id
     */

      getBlotIndex(blotDomNode){
        const subBlot = Quill.find(blotDomNode);
        return this.editor.getIndex(subBlot);
      },

    /**
     * Returns the dom node of the blot based on the message id or null
     */

      getBlotNode(){
        return document.querySelector(`.ql-label[data-id="MMS_LINK_${this.messageId}"]`);
      },

    /**
     * Adds the tag of %{MMS_LINK} as a string or removes the blot based on the evaluation of substitutions.MMS_LINK.value.
     * Immediately following the evaluation, this.checkForSubstitutions is called to add the blot, if applicable.
     *
     * _Notes_
     * - If there is a value present for substitutions.MMS_LINK.value and smsText does not already have the tag, it is inserted.
     * - substitutions.MMS_LINK.value is null, remove the blot from the dom
     */

      handleSubtitutions(){
        let skip = false;
        if (this.substitutions.MMS_LINK?.value && this.smsText.indexOf('%{MMS_LINK}') === -1) {
          this.smsText += ' %{MMS_LINK} '
        } else if (this.substitutions.MMS_LINK.value === null) {
          const subLabelNode = this.getBlotNode();
          if (subLabelNode !== null) { subLabelNode.remove() }
          skip = true;
        }
        this.checkForSubstitutions(this.smsText, skip)
      },

      highlightTerm() {
        const blotNode = this.getBlotNode();
        if (blotNode) {
          const blotIdx = this.getBlotIndex(blotNode);
          blotIdx < this.alertedTerm.index ? this.alertedTerm.index += 1 : '';
        }
        this.editor.formatText(this.alertedTerm.index, this.alertedTerm.phrase.length, {'background': 'rgba(18,143,249,.5)'}, )
        this.updateMessageState('showBlacklistModal', true)
      },

      insertInTextArea(memberInput) {
        const cursorIndex = this.editor.getSelection(true) ? this.editor.getSelection(true).index : 0;
        this.editor.insertText(cursorIndex, memberInput);

        const updatedText = this.editor.getText()
        this.smsDisplay = updatedText
        this.smsText = updatedText
        this.checkForEmojis(updatedText)
        this.updateSms(updatedText, true)
      },

      loadQuill(isPlainText, isFirstLoad) {
        let scrollTop
        const options = {
          modules: {
            toolbar: false,
            subLabel: {}
          },
          theme: 'snow'
        }

        if (this.editor) {
          scrollTop = this.$el.querySelector('.sms-input.ql-container.ql-snow > div.ql-editor').scrollTop
          this.editor = null
          this.$forceUpdate()
        }

        this.editor = new Quill(this.$refs.editor, options)
        this.editor.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
          if(node.parentElement && node.parentElement.tagName === 'SPAN' && node.data === this.mockUrl) {
            return new Delta();
          }

          return delta;
        });

        this.editor.root.setAttribute('spellcheck', false)

        /**
         * KLUDGE: Re-creating Quill editor forces content to scroll to the top. We need to restore the scroll position to support features like the blacklist modal's display position
         */
        if(scrollTop) {
          this.$el.querySelector('.sms-input.ql-container.ql-snow > div.ql-editor').scrollTop = scrollTop
        }

        if (isFirstLoad && this.updatedSmsContent || !isPlainText) {
          isFirstLoad && this.editor.setText(this.smsDisplay)
          if (!isPlainText) { this.highlightTerm() }
        }

        this.editor.on('text-change', () => this.delaySmsFilter())
        this.editor.root.addEventListener('blur', () => this.delaySmsFilter())
      },

      onClearModal() {
        this.clearModal()
        this.loadQuill(true)
        this.filterBlacklistedTerms()
      },

      replaceBlacklistedTerm(newTerm) {
        const replacedWord = newTerm?.length ? `${this.alertedTerm.prefix}${newTerm}${this.alertedTerm.suffix}` : newTerm;
        this.editor.deleteText(this.alertedTerm.index, this.alertedTerm.phrase.length);
        this.editor.insertText(this.alertedTerm.index, replacedWord)
        this.onClearModal()
      },

      updateCampaign(data) {
        this.updateSms(this.updatedSmsContent, true);
        this.$emit('updateCampaign', data)
      },

      updateIgnoredTerms() {
        const newCursor = this.alertedTerm.index + this.alertedTerm.phrase.length
        this.ignoredTerms.push(this.alertedTerm.phrase)
        this.editor.removeFormat(this.alertedTerm.index, this.alertedTerm.phrase.length)
        this.onClearModal(newCursor)
      },

      updateInteraction(shouldValidate) {
        if (this.noUserInteraction) {
          this.noUserInteraction = false
          this.configUserInteractions({})
          if (shouldValidate) { this.validateSms() }
        }
      },

      updateMessageState(stateKey, stateItem) {
        this.$store.dispatch(`${this.messageModuleName}/UPDATE_MESSAGE_STATE`, { stateKey, stateItem })
      },

      updateSms($text, shouldUpdate) {
        const prevSmsContent = this.updatedSmsContent || ''
        $text = $text || ''
        if (prevSmsContent !== $text || shouldUpdate) {
          const url = this.updateUrl,
            methodType = 'patch',
            auto_campaign_id = this.autoCampaignId
          let text = $text || !!$text?.length ? $text : this.updatedSmsContent;

          /**
           * Checking if the domNode of `.ql-label` exists,
           *  if so, add the string of `%{MMS_LINK}` to the value so the backend knows where to add the URL
          */
          let blotDomNode = this.getBlotNode();
          if (blotDomNode && text.indexOf("%{MMS_LINK}") === -1 && this.editor) {
            text = spliceIntoString(text, this.getBlotIndex(blotDomNode), '%{MMS_LINK}');
          }
          const smsParams = {text, extension_link: this.hasExtensionLink, prefix_link: this.hasImageLink},
            params = auto_campaign_id ? {...smsParams, auto_campaign_id} : smsParams
            !shouldUpdate && this.validateSms()
          this.axiosMethod({methodType, url, params, onSuccess: this.afterUpdateSms, onError: this.errorUpdateSms})
        }
      },

      updateUserInteraction(shouldUpdate) {
        if (!this.noUserInteraction) { this.validateSms() }
        if (shouldUpdate && this.noUserInteraction) { this.updateInteraction(true) }
      },

      validateSms() {
        if (this.alertedTerm) { return }
        const result = this.smsText?.match(/\n/g) || '',
          emptyWithNewLine = this.smsText?.length === 1 && !!result,
          validSmsContent = !!this.smsText?.length && !emptyWithNewLine

        this.validSms = this.mediaCredits > 0 ? true : validSmsContent
        const itemObject = this.validSms || !this.smsSend ? {} : { smsValid: this.validSms }

        this.$store.dispatch('UPDATE_STATE_OBJECT', {itemKey: this.dispatchSmsItemKey, itemObject})
      },
    },

    created() {
      const prevSmsContent = this.updatedSmsContent
      this.smsDisplay = prevSmsContent
      this.smsText = prevSmsContent
      this.updateSms(prevSmsContent, true)

      if (prevSmsContent) {
        this.updateInteraction()
        this.checkForEmojis(prevSmsContent)
        this.checkForSubstitutions(prevSmsContent)
      } else {
        this.smsSend && this.configUserInteractions({ sms: true })
      }
    },

    mounted() {
      this.loadQuill(true, true)
    },

    beforeDestroy() {
      this.configUserInteractions({})
      this.$store.dispatch('UPDATE_STATE_OBJECT', { itemKey: this.dispatchSmsItemKey, itemObject: {} })
    },

    computed: {
      dispatchSmsItemKey: function () {
        return `sms${this.messageId}`
      },

      hasExtensionLink: function () {
        const extension = this.extensionType || 0
        return extension !== 0
      },

      hasImageLink: function () {
        return !!this.adAsLink || !!this.mmsAsLink
      },

      hasAdImageAsLink: function () {
        return !!this.adAsLink
      },

      hasMmsImageAsLink: function () {
        return !!this.mmsAsLink
      },

      updateUrl: function () {
        let routeType = 'auto_campaign_messages';

        if(this.isCampaign) {
          routeType = this.classicCampaignsEnabled ? 'classic_campaigns' : 'campaigns';
        }

        return `/api/v3/${routeType}/${this.messageId}/update_sms_content`
      },

      ...mapGetters(['classicCampaignsEnabled']),
      ...mapState({
        ad (state) { return state[this.messageModuleName]?.ad },
        adAsLink (state, getters) { return getters[`${this.messageModuleName}/adAsLink`] },
        adSuggestedText (state) { return state.message?.adSuggestedText },
        autoCampaignId (state) { return state[this.messageModuleName]?.autoCampaignId },
        blacklistedTerms (state) { return state.blacklistedTerms },
        extensionType (state) { return state[this.messageModuleName]?.extensionType },
        hasAd (state, getters) { return getters[`${this.messageModuleName}/hasAd`] },
        isCampaign (state, getters) { return getters[`${this.messageModuleName}/isCampaign`] },
        mediaCredits (state, getters) { return getters[`${this.messageModuleName}/mediaCredits`] },
        mmsAsLink (state, getters) { return getters[`${this.messageModuleName}/mmsAsLink`] },
        messageId (state) { return state[this.messageModuleName]?.messageId },
        mockUrl (state) { return state[this.messageModuleName]?.mockUrl },
        showBlacklistModal (state) { return state[this.messageModuleName]?.showBlacklistModal },
        updatedSmsContent (state) { return state[this.messageModuleName]?.updatedSmsContent },
      })
    },

    watch: {
      ad: function(){
        this.adAsLink && this.updateSms(this.updatedSmsContent, true)
      },
      adSuggestedText: function(){
        // Clear contents of editor
        this.editor.setContents([{ insert: '\n' }]);

        // // Insert the suggested text into the editor
        if (this.adSuggestedText) {
          this.insertInTextArea(this.adSuggestedText)
        }

      },
      hasAd: function () {
        this.updateUserInteraction(this.hasAd && !this.adAsLink)
      },

      hasExtensionLink: function () {
        this.updateSms(this.updatedSmsContent, true)
      },

      hasAdImageAsLink: function () {
        this.delayUpdateSms(this.updatedSmsContent, true)
      },

      hasMmsImageAsLink: function () {
        this.delayUpdateSms(this.updatedSmsContent, true)
      },

      mediaCredits: function () {
        this.updateUserInteraction(this.mediaCredits > 0)
      },

      smsSend: function () {
        this.updateUserInteraction(!this.smsSend)
        if (this.smsSend && this.noUserInteraction) { this.configUserInteractions({ sms: true }) }
      },

      specialCharacters: function () {
        this.checkForEmojis()
      }
    }
  }
</script>
